// src/modules/lodrive/UploadStore.js
// Basit bir global kuyruk yöneticisi (singleton per sessionKey)

class UploadStore {
    constructor(sessionKey) {
        this.sessionKey = sessionKey;
        this.items = []; // {id, file, path, size, status, targetFolderId}
        this.busy = false;
        this.stopRequested = false;
        this.listeners = new Set();
        this.onBatchComplete = null;
        this.onUpload = null; // dışarıdan atanır
        this._autoId = 0;
        this._loopRunning = false;
    }

    // ---- Abonelik ----
    subscribe(fn) {
        this.listeners.add(fn);
        // ilk anda mevcut state'i göndereyim
        try { fn(this.getSnapshot()); } catch { }
        return () => this.listeners.delete(fn);
    }
    notify() {
        const snap = this.getSnapshot();
        this.listeners.forEach((fn) => { try { fn(snap); } catch { } });
    }
    getSnapshot() {
        return {
            items: this.items,
            busy: this.busy,
            stopRequested: this.stopRequested
        };
    }

    // ---- Dış API ----
    setOnUpload(fn) {
        this.onUpload = typeof fn === 'function' ? fn : null;
    }

    setOnBatchComplete(fn) {
        this.onBatchComplete = typeof fn === 'function' ? fn : null;
    }

    enqueue(filesWithPaths, targetFolderId = null) {
        const toAdd = (filesWithPaths || []).map(fp => ({
            id: ++this._autoId,
            file: fp.file,
            path: fp.path || fp.file?.webkitRelativePath || fp.file?.name,
            size: fp.file?.size ?? 0,
            status: 'pending',
            targetFolderId
        }));

        if (this._isBatchFinished()) {
            // overwrite davranışı: yeni batch başlıyorsa eskileri temizle
            this.items = toAdd;
        } else {
            this.items = [...this.items, ...toAdd];
        }
        this.notify();
        this._ensureLoop();
    }

    stopGracefully() {
        if (!this.busy) return;
        this.stopRequested = true;
        this.notify();
    }

    resume() {
        this.stopRequested = false;
        this.notify();
        this._ensureLoop();
    }

    clearFinished() {
        this.items = this.items.filter(x => x.status === 'pending' || x.status === 'uploading');
        this.notify();
    }

    clearAll() {
        // Aman dikkat: aktif uploading varsa da temizler (componentte butonu disable etmiştik zaten)
        this.items = [];
        this.stopRequested = false;
        this.busy = false;
        this.notify();
    }

    // ---- İç yardımcılar (IMMUTABLE UPDATE) ----
    _setItemStatusById(id, nextStatus) {
        const idx = this.items.findIndex(x => x.id === id);
        if (idx === -1) return;

        // öğeyi kopyala, dizi referansını kopyala
        const updated = { ...this.items[idx], status: nextStatus };
        const nextArr = [...this.items];
        nextArr[idx] = updated;
        this.items = nextArr;
    }

    _isBatchFinished() {
        if (this.busy) return false;
        if (this.items.length === 0) return true;
        return this.items.every(x => x.status === 'success' || x.status === 'error');
    }

    _ensureLoop() {
        if (this._loopRunning) return;
        if (!this.onUpload) return;
        if (!this.items.some(x => x.status === 'pending')) return;

        this._loopRunning = true;
        this._runLoop()
            .catch(() => { })
            .finally(() => {
                this._loopRunning = false;
                if (!this.stopRequested && this.items.some(x => x.status === 'pending')) {
                    setTimeout(() => this._ensureLoop(), 0);
                }
            });
    }

    async _runLoop() {
        if (!this.onUpload) return;

        this.busy = true;
        this.notify();
        try {
            // SIRALI: her dosya tek tek uploading -> success/error
            for (const it of this.items) {
                if (this.stopRequested) break;
                if (it.status !== 'pending') continue;

                // uploading'e geçir (immutable)
                this._setItemStatusById(it.id, 'uploading');
                this.notify();

                let ok = false;
                try {
                    ok = await this.onUpload({
                        file: it.file,
                        path: it.path,
                        targetFolderId: it.targetFolderId
                    });
                } catch {
                    ok = false;
                }

                // sonucu işle (immutable)
                this._setItemStatusById(it.id, ok ? 'success' : 'error');
                this.notify();

                if (this.stopRequested) break;
            }
        } finally {
            this.busy = false;
            this.notify();
            try {
                this.onBatchComplete && this.onBatchComplete();
            } catch { /* yut */ }
        }
    }
}

// sessionKey -> store map
const _stores = new Map();
export function getUploadStore(sessionKey = 'lodrive-global') {
    if (!_stores.has(sessionKey)) {
        _stores.set(sessionKey, new UploadStore(sessionKey));
    }
    return _stores.get(sessionKey);
}